import { Injectable } from '@angular/core';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseNavigationItem } from '@fuse/types';
import { ForfettappApiClient } from '@shared/forfettapp-api-client';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

/** Risposta dell'endpoint consolidato GET /backoffice/badge-counts */
export interface BadgeCounts {
    entiCreditoriDaGestire: number;
    atecoCodiciIntrastatDaGestire: number;
    delegheInpsDaGestire: number;
    aggiornamentoDatiInpsDaGestire: number;
    contribuzioniInpsDaGestire: number;
    intrastatToDo: boolean;
    registrazioniSpidDaGestire: number;
    scadenzeDaInviare: number;
    fattureFncInInvioInCorsoUltimi3Giorni: number;
    documentiFncScartati: number;
    bolliInErrore: number;
}

@Injectable({ providedIn: 'root' })
export class BadgeService {
    constructor(
        private apiClient: ForfettappApiClient,
        private fuseNavigationService: FuseNavigationService,
    ) {}

    /** Chiama l'endpoint consolidato e popola TUTTI i badge di navigazione */
    loadAllBadges(): Observable<BadgeCounts> {
        return this.apiClient.get<BadgeCounts>('/backoffice/badge-counts').pipe(
            map(counts => {
                this.updateBadge('enti-creditori', counts.entiCreditoriDaGestire, true);
                this.updateBadge('intrastat', counts.atecoCodiciIntrastatDaGestire, true);
                this.updateBadge('gestione-tabelle', counts.entiCreditoriDaGestire + counts.atecoCodiciIntrastatDaGestire, false, 'da fare');

                this.updateBadge('gestione-inps-deleghe', counts.delegheInpsDaGestire, true);
                this.updateBadge('gestione-inps-aggiornamento', counts.aggiornamentoDatiInpsDaGestire, true);
                this.updateBadge('gestione-inps-controllo-contribuzioni', counts.contribuzioniInpsDaGestire, true);
                this.updateParentBadge(
                    'gestione-inps',
                    counts.delegheInpsDaGestire + counts.aggiornamentoDatiInpsDaGestire + counts.contribuzioniInpsDaGestire,
                );

                this.updateBadge('gestione-invii-intrastat', counts.intrastatToDo ? 1 : 0, false, 'To do');

                this.updateBadge('registrazione-spid', counts.registrazioniSpidDaGestire, true);
                this.updateParentBadge('forfettari-in-registrazione', counts.registrazioniSpidDaGestire);

                this.updateBadge('gestione-scadenze', counts.scadenzeDaInviare, true);

                this.updateBadge('invii-sdi-fnc', counts.fattureFncInInvioInCorsoUltimi3Giorni, true);
                this.updateParentBadge('fatturazione-elettronica', counts.fattureFncInInvioInCorsoUltimi3Giorni);

                this.updateBadge('documenti-attivi', counts.documentiFncScartati, true);
                this.updateParentBadge('fnc', counts.documentiFncScartati);

                this.updateBadge('gestione-bolli', counts.bolliInErrore, true);

                return counts;
            }),
            catchError(() => of(null)),
        );
    }

    private updateBadge(id: string, count: number, showCount: boolean, label?: string) {
        const item = this.fuseNavigationService.getNavigationItem(id);
        if (!item) return;
        const title = count > 0 ? (showCount ? count.toString() : label || '') : null;
        this.fuseNavigationService.updateNavigationItem(id, {
            badge: count > 0 ? { title, bg: 'red' } : null,
        });
    }

    private updateParentBadge(id: string, count: number) {
        const parent = this.fuseNavigationService.getNavigationItem(id) as FuseNavigationItem;
        if (!parent) return;
        // Mantiene il badge esistente: non lo cancella se count è 0 (comportamento originale)
        if (parent.badge && count === 0) return;
        this.fuseNavigationService.updateNavigationItem(id, {
            badge: count > 0 ? { title: '', bg: 'red' } : null,
        });
    }
}
